import { useState, useRef, useEffect, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import {
  ChevronDown,
  ChevronRight,
  Eye,
  FileText,
  GitBranchPlus,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AppLink } from "@/components/app-link";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { useProject } from "@/components/projects";
import { PageHeader } from "@/components/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { EmptyState } from "@/components/empty-state";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CreateTakeoffRevisionDialog } from "@/features/takeoff-pilot/create-takeoff-revision-dialog";
import {
  getTakeoffRevisionNumber,
  groupTakeoffJobsByRevision,
} from "@/lib/takeoff-revisions";
import { cn } from "@/lib/utils";
import { useAppNavigate } from "@/lib/use-app-navigate";

export const Route = createFileRoute("/takeoffPilot/")({
  component: TakeoffPilotPage,
});

const USER_STATUS_OPTIONS = [
  { value: "initiated", label: "Initiated" },
  { value: "ready_for_review", label: "Ready for Review" },
  { value: "done", label: "Done" },
] as const;

type TakeoffUserStatus = (typeof USER_STATUS_OPTIONS)[number]["value"];

const DEFAULT_USER_STATUS: TakeoffUserStatus = "initiated";

const EASTERN_TIME_ZONE = "America/New_York";

const monthDayFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: EASTERN_TIME_ZONE,
  month: "short",
  day: "numeric",
});

function formatMonthDay(timestamp?: number): string {
  if (!timestamp) {
    return "-";
  }
  return monthDayFormatter.format(new Date(timestamp));
}

function getTakeoffStatusLabel(status: string): string {
  switch (status) {
    case "initiated":
      return "Initiated";
    case "ready_for_review":
      return "Ready for Review";
    case "done":
      return "Done";
    case "copying_revision":
      return "Copying Revision";
    case "extracting_schedule":
      return "Extracting Schedule";
    case "ready_for_tag_selection":
      return "Ready for Tag Selection";
    case "indexing_finish_plan":
      return "Indexing Finish Plan";
    case "resolving_tag_counts":
      return "Resolving Tag Counts";
    case "ready_for_results":
      return "Ready for Results";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    default:
      return status.replaceAll("_", " ");
  }
}

function getTakeoffStatusAccentClasses(status: string): string {
  switch (status) {
    case "initiated":
    case "ready_for_tag_selection":
      return "bg-status-pending";
    case "ready_for_review":
    case "ready_for_results":
      return "bg-status-review";
    case "done":
    case "completed":
      return "bg-status-completed";
    case "failed":
      return "bg-status-error";
    default:
      return "bg-status-processing";
  }
}

function useInlineEdit(initialValue: string, onSave: (value: string) => Promise<void>) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== initialValue) {
      try {
        await onSave(trimmed);
      } catch {
        setValue(initialValue);
      }
    } else {
      setValue(initialValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    } else if (e.key === "Escape") {
      setValue(initialValue);
      setIsEditing(false);
    }
  };

  return {
    isEditing,
    setIsEditing,
    value,
    setValue,
    inputRef,
    handleSave,
    handleKeyDown,
  };
}

interface TakeoffJobListItem {
  _id: Id<"takeoffPilotJobs">;
  projectId: Id<"projects">;
  name: string;
  status: string;
  userStatus?: TakeoffUserStatus;
  createdByName: string | null;
  lastWorkedOnAt?: number;
  createdAt: number;
  revisionRootJobId?: Id<"takeoffPilotJobs">;
  revisionSourceJobId?: Id<"takeoffPilotJobs">;
  revisionNumber?: number;
}

function getTakeoffDisplayStatus(job: TakeoffJobListItem): string {
  return job.status === "copying_revision" ? job.status : (job.userStatus ?? DEFAULT_USER_STATUS);
}

export function TakeoffPilotPage() {
  const navigate = useAppNavigate();
  const { projectId, project, isLoading: isProjectLoading } = useProject();
  const { data: jobs, isPending } = useQuery(convexQuery(api.takeoffPilot.jobs.listAll, {}));
  const removeJob = useMutation(api.takeoffPilot.jobs.remove);
  const updateName = useMutation(api.takeoffPilot.jobs.updateName);
  const updateUserStatus = useMutation(api.takeoffPilot.jobs.updateUserStatus);
  const [deletingJobIds, setDeletingJobIds] = useState<Set<string>>(() => new Set());
  const [openRevisionGroupIds, setOpenRevisionGroupIds] = useState<Set<string>>(() => new Set());
  const [revisionDialogJob, setRevisionDialogJob] = useState<TakeoffJobListItem | null>(null);
  const visibleJobs = useMemo(
    () => (jobs ?? []).filter((job) => !projectId || job.projectId === projectId),
    [jobs, projectId]
  );
  const revisionGroups = useMemo(
    () => groupTakeoffJobsByRevision(visibleJobs as TakeoffJobListItem[]),
    [visibleJobs]
  );

  const handleToggleRevisionHistory = (rootId: string) => {
    setOpenRevisionGroupIds((current) => {
      const next = new Set(current);
      if (next.has(rootId)) {
        next.delete(rootId);
      } else {
        next.add(rootId);
      }
      return next;
    });
  };

  const handleStartRevPilotReview = (job: TakeoffJobListItem) => {
    navigate({
      to: "/revPilot/new",
      search: { sourceTakeoffJobId: job._id },
      title: "New RevPilot Review",
    });
  };

  const handleNewTakeoff = () => {
    navigate({ to: "/takeoffPilot/new", title: "New TakeoffPilot" });
  };

  const handleDeleteJob = async (jobId: Id<"takeoffPilotJobs">) => {
    setDeletingJobIds((current) => {
      const next = new Set(current);
      next.add(String(jobId));
      return next;
    });

    try {
      await removeJob({ jobId });
      toast.success("Job deleted");
    } catch (error) {
      toast.error("Failed to delete job");
      console.error(error);
    } finally {
      setDeletingJobIds((current) => {
        const next = new Set(current);
        next.delete(String(jobId));
        return next;
      });
    }
  };

  const handleUpdateName = async (jobId: Id<"takeoffPilotJobs">, name: string) => {
    try {
      await updateName({ jobId, name });
      toast.success("Name updated");
    } catch (error) {
      toast.error("Failed to update name");
      console.error(error);
      throw error;
    }
  };

  const handleUpdateStatus = async (
    jobId: Id<"takeoffPilotJobs">,
    userStatus: TakeoffUserStatus
  ) => {
    try {
      await updateUserStatus({
        jobId,
        userStatus,
      });
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
      console.error(error);
    }
  };

  if (isProjectLoading || isPending) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="TakeoffPilot"
          description={project?.name ?? "Loading project..."}
          actions={
            <Button onClick={handleNewTakeoff}>
              <Plus className="size-4" />
              New TakeoffPilot Job
            </Button>
          }
        />
        <div className="flex items-center justify-center p-12">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (visibleJobs.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="TakeoffPilot"
          description={project?.name ?? "Select a project"}
          actions={
            <Button onClick={handleNewTakeoff}>
              <Plus className="size-4" />
              New TakeoffPilot Job
            </Button>
          }
        />
        <EmptyState
          icon={FileText}
          title="No TakeoffPilot jobs yet"
          description="Create a TakeoffPilot job from project documents or uploaded plan PDFs to start extracting takeoff quantities."
          action={{ label: "Create New TakeoffPilot Job", onClick: handleNewTakeoff }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="TakeoffPilot"
        description={project?.name ?? "Select a project"}
        actions={
          <Button onClick={handleNewTakeoff}>
            <Plus className="size-4" />
            New TakeoffPilot Job
          </Button>
        }
      />
      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[70px]">
                <span className="sr-only">View</span>
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {revisionGroups.flatMap((group) => {
              const isHistoryOpen = openRevisionGroupIds.has(group.rootId);
              const primaryJob = group.latestJob;
              const primaryDisplayStatus = getTakeoffDisplayStatus(primaryJob);
              const primaryStatusLabel = getTakeoffStatusLabel(primaryDisplayStatus);
              const primaryRow = (
                <JobRow
                  key={primaryJob._id}
                  job={primaryJob}
                  isDeleting={deletingJobIds.has(String(primaryJob._id))}
                  displayStatus={primaryDisplayStatus}
                  statusLabel={primaryStatusLabel}
                  revisionLevel={0}
                  showRevisionBadge={
                    group.historyJobs.length > 0 || getTakeoffRevisionNumber(primaryJob) > 1
                  }
                  revisionHistoryCount={group.historyJobs.length}
                  isRevisionHistoryOpen={isHistoryOpen}
                  onToggleRevisionHistory={() => handleToggleRevisionHistory(group.rootId)}
                  onCreateRevision={setRevisionDialogJob}
                  onStartRevPilotReview={handleStartRevPilotReview}
                  onDelete={handleDeleteJob}
                  onUpdateName={handleUpdateName}
                  onUpdateStatus={handleUpdateStatus}
                />
              );

              if (!isHistoryOpen) {
                return [primaryRow];
              }

              return [
                primaryRow,
                ...group.historyJobs.map((job) => {
                  const isDeleting = deletingJobIds.has(String(job._id));
                  const displayStatus = getTakeoffDisplayStatus(job);
                  const statusLabel = getTakeoffStatusLabel(displayStatus);

                  return (
                    <JobRow
                      key={job._id}
                      job={job}
                      isDeleting={isDeleting}
                      displayStatus={displayStatus}
                      statusLabel={statusLabel}
                      revisionLevel={1}
                      showRevisionBadge={true}
                      onCreateRevision={setRevisionDialogJob}
                      onStartRevPilotReview={handleStartRevPilotReview}
                      onDelete={handleDeleteJob}
                      onUpdateName={handleUpdateName}
                      onUpdateStatus={handleUpdateStatus}
                    />
                  );
                }),
              ];
            })}
          </TableBody>
        </Table>
      </div>
      <CreateTakeoffRevisionDialog
        jobId={revisionDialogJob?._id ?? null}
        jobName={revisionDialogJob?.name}
        open={revisionDialogJob !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRevisionDialogJob(null);
          }
        }}
        onCreated={(jobId) =>
          navigate({
            to: "/takeoffPilot/$jobId",
            params: { jobId },
            title: "Takeoff Revision",
          })
        }
      />
    </div>
  );
}

interface JobRowProps {
  job: TakeoffJobListItem;
  isDeleting: boolean;
  displayStatus: string;
  statusLabel: string;
  revisionLevel: number;
  showRevisionBadge: boolean;
  revisionHistoryCount?: number;
  isRevisionHistoryOpen?: boolean;
  onToggleRevisionHistory?: () => void;
  onCreateRevision: (job: TakeoffJobListItem) => void;
  onStartRevPilotReview: (job: TakeoffJobListItem) => void;
  onDelete: (jobId: Id<"takeoffPilotJobs">) => Promise<void>;
  onUpdateName: (jobId: Id<"takeoffPilotJobs">, name: string) => Promise<void>;
  onUpdateStatus: (jobId: Id<"takeoffPilotJobs">, userStatus: TakeoffUserStatus) => Promise<void>;
}

function JobRow({
  job,
  isDeleting,
  displayStatus,
  statusLabel,
  revisionLevel,
  showRevisionBadge,
  revisionHistoryCount = 0,
  isRevisionHistoryOpen = false,
  onToggleRevisionHistory,
  onCreateRevision,
  onStartRevPilotReview,
  onDelete,
  onUpdateName,
  onUpdateStatus,
}: JobRowProps) {
  const inlineEdit = useInlineEdit(job.name, (name) => onUpdateName(job._id, name));
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <TableRow className={cn("group", revisionLevel > 0 && "bg-muted/15")}>
      <TableCell>
        <div className="flex items-center gap-1">
          <AppLink
            to="/takeoffPilot/$jobId"
            params={{ jobId: job._id }}
            title={job.name}
          >
            <Button variant="ghost" size="icon-sm" className="h-8 w-8">
              <Eye className="size-4" />
              <span className="sr-only">View {job.name}</span>
            </Button>
          </AppLink>
          {revisionHistoryCount > 0 ? (
            <Button
              variant="ghost"
              size="icon-sm"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              aria-expanded={isRevisionHistoryOpen}
              aria-label={`${isRevisionHistoryOpen ? "Hide" : "Show"} revision history for ${job.name}`}
              onClick={onToggleRevisionHistory}
            >
              {isRevisionHistoryOpen ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </Button>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="font-medium">
        {inlineEdit.isEditing ? (
          <Input
            ref={inlineEdit.inputRef}
            value={inlineEdit.value}
            onChange={(e) => inlineEdit.setValue(e.target.value)}
            onBlur={() => void inlineEdit.handleSave()}
            onKeyDown={inlineEdit.handleKeyDown}
            className="h-8 w-full max-w-xs"
          />
        ) : (
          <div
            className={cn(
              "flex items-center gap-2",
              revisionLevel > 0 && "pl-7"
            )}
          >
            {showRevisionBadge ? (
              <Badge variant={revisionLevel > 0 ? "secondary" : "outline"} className="px-2 py-0.5">
                R{getTakeoffRevisionNumber(job)}
              </Badge>
            ) : null}
            <span className="text-foreground">{job.name}</span>
            <Button
              variant="ghost"
              size="icon-sm"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 focus:opacity-100"
              onClick={() => inlineEdit.setIsEditing(true)}
            >
              <Pencil className="size-3" />
              <span className="sr-only">Edit name</span>
            </Button>
          </div>
        )}
      </TableCell>
      <TableCell>
        <Select
          value={job.userStatus ?? DEFAULT_USER_STATUS}
          onValueChange={(value) => {
            void onUpdateStatus(job._id, value as TakeoffUserStatus);
          }}
        >
          <SelectTrigger
            className={cn(
              "h-8 w-[190px] rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground shadow-none",
              "hover:bg-accent/40 focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/20",
              "text-left"
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span
                className={cn(
                  "size-2 rounded-full",
                  getTakeoffStatusAccentClasses(displayStatus)
                )}
              />
              <span className="truncate">{statusLabel}</span>
            </span>
          </SelectTrigger>
          <SelectContent
            align="start"
            collisionPadding={8}
            position="popper"
            sideOffset={4}
          >
            {USER_STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {job.createdByName ?? "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatMonthDay(job.createdAt)}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatMonthDay(job.lastWorkedOnAt ?? job.createdAt)}
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={isDeleting}
              className="text-muted-foreground hover:text-foreground"
              aria-label={`Actions for ${job.name}`}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                onCreateRevision(job);
              }}
              className="gap-2"
            >
              <GitBranchPlus className="size-4" />
              Create revision
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                onStartRevPilotReview(job);
              }}
              className="gap-2"
            >
              <FileText className="size-4" />
              Start RevPilot review
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                setIsDeleteDialogOpen(true);
              }}
              disabled={isDeleting}
              className="gap-2 text-destructive focus:text-destructive"
            >
              <Trash2 className="size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ConfirmationDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          title="Delete TakeoffPilot Job"
          description={`Are you sure you want to delete "${job.name}"? This action cannot be undone.`}
          onConfirm={() => {
            void onDelete(job._id);
          }}
          disabled={isDeleting}
          confirmLabel={isDeleting ? "Deleting..." : "Delete"}
          confirmVariant="destructive"
        />
      </TableCell>
    </TableRow>
  );
}
